<script lang="ts" generics="ROW, ITEM">
    import type {Readable} from 'svelte/store';
    import type {Table} from '@tanstack/svelte-table';
    import type {GridState} from 'shared/ui-widgets/grid/types';
    import type {FondFieldTypeDefinitions} from 'src/features/record/field-types/types';
    import type {Pagination, PaginationData} from 'src/features/search/search-manager/pagination';
    import {states} from 'shared/constants';
    import {KpSearchFieldTypeFiltersContainer} from 'src/features/search/kp-search-field-type-filter';
    import {getLocalization} from 'src/core';
    import {onDestroy} from 'svelte';
    import {cleanup, exists} from 'shared/utils/custom-utils';
    import {createInfiniteScrollContext} from 'shared/ui-widgets/grid/infinite-scroll.context';
    import KpLoadingBlock from 'shared/components/kp-loading/KpLoadingBlock.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import RecordGridBottomBar from './RecordGridBottomBar.svelte';

    export let pagination: Pagination<ITEM>;
    export let table$: Readable<Table<ROW>>;
    export let tableState$: Readable<GridState>;
    export let fondId: number;
    export let fieldTypeDefinitions: FondFieldTypeDefinitions;
    export let infiniteScroll: boolean;

    const localize = getLocalization();

    let paginationData: PaginationData<ITEM> | undefined;
    let initialLoading = true;
    const paginationDataSubscription = pagination.getPaginationData$().subscribe((data) => {
        paginationData = data;

        if (paginationData.state.distantPage === states.NORMAL && initialLoading) {
            initialLoading = false;
        }
    });

    // Infinite scroll setup
    const infiniteScrollContext = createInfiniteScrollContext(infiniteScroll);
    let scrollListenerUnsubscribe: () => void;
    // Register scroll listener when grid element is available
    const gridElementUnsubscribe = infiniteScrollContext.gridElementRef$.subscribe((element) => {
        if (!infiniteScroll || !exists(element) || exists(scrollListenerUnsubscribe)) {
            return;
        }

        element.addEventListener('scroll', handleInfiniteScrollToBottom);
        scrollListenerUnsubscribe = () => element.removeEventListener('scroll', handleInfiniteScrollToBottom);
    });

    // Handle infinite scroll to bottom
    let lastScrollTop = 0;
    const handleInfiniteScrollToBottom = (event: Event) => {
        const element = event.target as HTMLElement;

        const bottomOffset = 400; // Load more data when we are 400px from the bottom

        const isUniqueScroll = lastScrollTop !== element.scrollTop;
        const isScrolledAtBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - bottomOffset;
        const hasNextPage = exists(paginationData) && paginationData.hasNextPage;
        const isNotLoading = paginationData.state.distantPage !== states.PROCESSING;

        if (isUniqueScroll && isScrolledAtBottom && hasNextPage && isNotLoading) {
            infiniteScrollContext.setLoading(true);
            pagination.requestNextPage();
        }

        if (isNotLoading) {
            lastScrollTop = element.scrollTop;
        }
    };

    // Update infinite scroll loading state
    $: if (infiniteScroll && exists(paginationData)) {
        infiniteScrollContext.setLoading(!initialLoading && paginationData.state.distantPage === states.PROCESSING);
    }

    onDestroy(() => {
        cleanup(paginationDataSubscription, scrollListenerUnsubscribe, gridElementUnsubscribe);
    });
</script>

{#if exists(paginationData)}
    <div class="grid-search-results-container">
        <div class="grid-search-results">
            {#if initialLoading}
                <div class="floating-container">
                    <KpLoadingBlock/>
                </div>
            {/if}

            {#if paginationData.loadPageErrorMessage}
                <div class="floating-container">
                    <IconedContent icon="exclamation"
                                   iconColor="var(--danger-red)"
                                   align="center"
                                   justify="center"
                                   orientation="vertical">

                        {paginationData.loadPageErrorMessage}
                    </IconedContent>
                </div>
            {/if}

            <KpSearchFieldTypeFiltersContainer {fondId} {fieldTypeDefinitions}>
                <slot/>
            </KpSearchFieldTypeFiltersContainer>

            {#if !paginationData.loadPageErrorMessage && paginationData.state.page === states.NORMAL && paginationData.items.length === 0}
                <div class="floating-container">
                    <IconedContent icon="info"
                                   align="center"
                                   justify="center"
                                   orientation="vertical">

                        {localize(/* @kp-localization commons.ZadnePolozky */ 'commons.ZadnePolozky')}
                    </IconedContent>
                </div>
            {/if}
        </div>

        {#if exists(paginationData)}
            <RecordGridBottomBar {table$} {tableState$} {pagination} {paginationData}/>
        {/if}
    </div>
{/if}

<style lang="less">
    @import (reference) "styles/portaro-erp.less";

    .grid-search-results-container {
        position: relative;

        .floating-container {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
        }
    }

    .grid-search-results-container,
    .grid-search-results {
        .flex-grow();

        :global {
            .kp-generic-grid-container {
                .flex-grow();

                border-radius: 0 !important;
                border-left: none !important;
                border-right: none !important;
                border-bottom: none !important;

                .kp-generic-grid {
                    .flex-grow();
                }
            }
        }
    }
</style>